import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { setLanguageGetter } from "@workspace/api-client-react";
import en from "./locales/en";
import vi from "./locales/vi";

/**
 * App i18n singleton.
 *
 * Vietnamese is the DEFAULT language regardless of the browser locale; the
 * user's choice is remembered in localStorage. When logged in, the choice is
 * also synced to the account (see LanguageSwitcher) so backend
 * emails/notifications render in the same language.
 *
 * Translations are split into namespaces (one JSON file per app area) so work
 * can proceed area-by-area without merge collisions. Missing keys fall back to
 * English, and un-migrated hardcoded strings simply render as-is — the app is
 * never left broken mid-migration.
 */
export const SUPPORTED_LANGS = ["en", "vi"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

export const LANG_STORAGE_KEY = "creative-adapt-lang";
export const DEFAULT_LANG: Lang = "vi";

export function isLang(v: unknown): v is Lang {
  return v === "en" || v === "vi";
}

function initialLang(): Lang {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    /* localStorage may be unavailable (private mode / SSR) */
  }
  return DEFAULT_LANG;
}

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources: { en, vi },
    lng: initialLang(),
    // Missing keys still fall back to English (the most complete catalog),
    // independent of the Vietnamese default UI language.
    fallbackLng: "en",
    defaultNS: "common",
    ns: [
      "common",
      "marketing",
      "auth",
      "app",
      "wizard",
      "billing",
      "assetDetail",
      "creativeSpace",
      "customerReview",
      "admin",
    ],
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    react: { useSuspense: false },
  });
}

/** Change the active language and persist the choice to localStorage. */
export function setLang(lang: Lang): void {
  if (!isLang(lang)) return;
  void i18n.changeLanguage(lang);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* ignore persistence failures */
    }
  }
}

export function getLang(): Lang {
  return isLang(i18n.language) ? i18n.language : DEFAULT_LANG;
}

// Attach the active language to every API request so the backend can localize
// its responses (error messages, etc.) to match the user's chosen language.
setLanguageGetter(() => getLang());

export default i18n;
